import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addPendingItem,
  updatePendingItem,
  removePendingItem,
  addShipmentItem,
  updateShipmentItem,
  deleteShipmentItem,
} from '../../store/slices/shipmentTrackerSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import ArrowBack from '../../components/ArrowBack';
import type { ShipmentItemRequest } from '../../api/generated/models/ShipmentItemRequest';

interface ShipmentItemFormPageProps {
  isEditing?: boolean;
  /** Index into pendingItems when shipment has no id yet */
  pendingIndex?: number;
}

const EMPTY_FORM: ShipmentItemRequest = {
  productName: '',
  sku: '',
  gtin: null,
  articleNumberSupplier: null,
  units: null,
  unitsPerCarton: null,
  cartons: null,
  weightPerCartonKg: null,
  lengthCm: null,
  widthCm: null,
  heightCm: null,
  grossWeightKg: null,
  netWeightKg: null,
  singleUnitPrice: null,
  customsDescription: null,
  mainMaterialComposition: null,
  intendedUse: null,
  hsCode6: null,
  cnCode8: null,
  taricCode10: null,
};

function numOrNull(val: string): number | null {
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

function intOrNull(val: string): number | null {
  const n = parseInt(val, 10);
  return isNaN(n) ? null : n;
}

/** ceil(units / units per carton) — null when either input is missing (carton_calc.md). */
function deriveCartons(
  units?: number | null,
  unitsPerCarton?: number | null,
): number | null {
  return units != null && unitsPerCarton != null && unitsPerCarton > 0
    ? Math.ceil(units / unitsPerCarton)
    : null;
}

/**
 * A stored carton count that disagrees with the derivation was set deliberately, so it must be
 * preserved when the form loads rather than snapped back to the calculated value.
 */
function isCartonsOverride(
  cartons?: number | null,
  units?: number | null,
  unitsPerCarton?: number | null,
): boolean {
  const derived = deriveCartons(units, unitsPerCarton);
  return derived !== null && cartons != null && cartons !== derived;
}

const ShipmentItemFormPage: React.FC<ShipmentItemFormPageProps> = ({
  isEditing = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { shipmentId, itemId } = useParams<{
    shipmentId?: string;
    itemId?: string;
  }>();

  const pendingItems = useAppSelector(
    (state) => state.shipmentTracker.pendingItems,
  );
  const detailShipment = useAppSelector(
    (state) => state.shipmentTracker.detail.shipment,
  );

  const [form, setForm] = useState<ShipmentItemRequest>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Determine if this is a "pending" (not-yet-saved shipment) context
  // shipmentId === "new" means we're on the create page, items are staged locally
  const isNewShipment = !shipmentId || shipmentId === 'new';

  // pendingIndex is carried via location state when navigating from the create page.
  const location = useLocation();
  const pendingIndex = (location.state as { pendingIndex?: number } | null)
    ?.pendingIndex;

  // ".../new/items/add" serves both add and edit for staged items, so the isEditing prop is
  // false when editing one — only pendingIndex tells them apart.
  const editing = isEditing || (isNewShipment && pendingIndex !== undefined);

  useEffect(() => {
    if (editing) {
      if (isNewShipment && pendingIndex !== undefined) {
        // Load from pending staging
        const staged = pendingItems[pendingIndex];
        if (staged) {
          setForm(staged);
          setCartonsOverridden(
            isCartonsOverride(
              staged.cartons,
              staged.units,
              staged.unitsPerCarton,
            ),
          );
        }
      } else if (shipmentId && itemId && detailShipment?.items) {
        // Load from saved shipment detail
        const existing = detailShipment.items.find((it) => it.id === itemId);
        if (existing) {
          setForm({
            productName: existing.productName,
            sku: existing.sku,
            gtin: existing.gtin ?? null,
            articleNumberSupplier: existing.articleNumberSupplier ?? null,
            units: existing.units ?? null,
            unitsPerCarton: existing.unitsPerCarton ?? null,
            cartons: existing.cartons ?? null,
            weightPerCartonKg:
              existing.weightPerCartonKg != null
                ? Number(existing.weightPerCartonKg)
                : null,
            lengthCm:
              existing.lengthCm != null ? Number(existing.lengthCm) : null,
            widthCm: existing.widthCm != null ? Number(existing.widthCm) : null,
            heightCm:
              existing.heightCm != null ? Number(existing.heightCm) : null,
            grossWeightKg:
              existing.grossWeightKg != null
                ? Number(existing.grossWeightKg)
                : null,
            netWeightKg:
              existing.netWeightKg != null
                ? Number(existing.netWeightKg)
                : null,
            singleUnitPrice:
              existing.singleUnitPrice != null
                ? Number(existing.singleUnitPrice)
                : null,
            customsDescription: existing.customsDescription ?? null,
            mainMaterialComposition: existing.mainMaterialComposition ?? null,
            intendedUse: existing.intendedUse ?? null,
            hsCode6: existing.hsCode6 ?? null,
            cnCode8: existing.cnCode8 ?? null,
            taricCode10: existing.taricCode10 ?? null,
          });
          setCartonsOverridden(
            isCartonsOverride(
              existing.cartons,
              existing.units,
              existing.unitsPerCarton,
            ),
          );
        }
      }
    }
  }, [
    editing,
    isNewShipment,
    pendingIndex,
    pendingItems,
    shipmentId,
    itemId,
    detailShipment,
  ]);

  const set = (
    field: keyof ShipmentItemRequest,
    value: string | number | null,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Cartons are derived from units ÷ units per carton, rounding up so a partial last carton still
   * counts. The user may override for exceptions; the override is kept and
   * flagged rather than silently recalculated, because cartons drive the volume allocation basis
   * and every per-carton figure.
   */
  const calculatedCartons = deriveCartons(form.units, form.unitsPerCarton);

  const partialCartonUnits =
    form.units != null && form.unitsPerCarton != null && form.unitsPerCarton > 0
      ? form.units % form.unitsPerCarton
      : 0;

  const [cartonsOverridden, setCartonsOverridden] = useState(false);

  // Keep the derived value in the field until the user deliberately takes it over.
  useEffect(() => {
    if (cartonsOverridden || calculatedCartons === null) return;
    setForm((prev) =>
      prev.cartons === calculatedCartons
        ? prev
        : { ...prev, cartons: calculatedCartons },
    );
  }, [calculatedCartons, cartonsOverridden]);

  // Linked price calculation: unit price ↔ carton price
  const [cartonPriceDisplay, setCartonPriceDisplay] = useState('');
  useEffect(() => {
    if (
      form.singleUnitPrice != null &&
      form.unitsPerCarton != null &&
      form.unitsPerCarton > 0
    ) {
      setCartonPriceDisplay(
        (form.singleUnitPrice * form.unitsPerCarton).toFixed(2),
      );
    } else {
      setCartonPriceDisplay('');
    }
  }, [form.singleUnitPrice, form.unitsPerCarton]);

  const handleCartonPriceChange = (val: string) => {
    setCartonPriceDisplay(val);
    if (form.unitsPerCarton && form.unitsPerCarton > 0) {
      const cartonPrice = parseFloat(val);
      if (!isNaN(cartonPrice)) {
        set('singleUnitPrice', cartonPrice / form.unitsPerCarton);
      }
    }
  };

  const totalItemValue =
    form.singleUnitPrice != null && form.units != null
      ? (form.singleUnitPrice * form.units).toFixed(2)
      : null;

  const validate = () => {
    if (!form.productName.trim()) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_item_error_name_required'),
        }),
      );
      return false;
    }
    if (!form.sku.trim()) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('shipment_item_error_sku_required'),
        }),
      );
      return false;
    }
    return true;
  };

  const goBack = () => navigate(-1);

  const handleAddItem = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isNewShipment) {
        // Stage locally - shipment doesn't have an id yet
        dispatch(addPendingItem(form));
        navigate(-1);
      } else if (shipmentId) {
        await dispatch(addShipmentItem({ shipmentId, data: form })).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_item_added'),
          }),
        );
        navigate(-1);
      }
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
    }
  };

  const handleSaveItem = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isNewShipment && pendingIndex !== undefined) {
        dispatch(updatePendingItem({ index: pendingIndex, data: form }));
        navigate(-1);
      } else if (shipmentId && itemId) {
        await dispatch(
          updateShipmentItem({ shipmentId, itemId, data: form }),
        ).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_item_saved'),
          }),
        );
        navigate(-1);
      }
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      if (isNewShipment && pendingIndex !== undefined) {
        dispatch(removePendingItem(pendingIndex));
        navigate(-1);
      } else if (shipmentId && itemId) {
        await dispatch(deleteShipmentItem({ shipmentId, itemId })).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            title: t('success'),
            message: t('shipment_item_deleted'),
          }),
        );
        navigate(-1);
      }
    } catch {
      // error notification fired in thunk
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page shipment-item-form-page">
      <div className="page-header">
        <ArrowBack onClick={goBack} />
        <h1>
          {editing
            ? t('shipment_item_edit_title')
            : t('shipment_item_add_title')}
        </h1>
      </div>

      {/* Product identity */}
      <div className="form-card">
        <h2 className="form-card__title">
          {t('shipment_item_section_identity')}
        </h2>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_product_name')} *
            <input
              className="input"
              value={form.productName}
              onChange={(e) => set('productName', e.target.value)}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_sku')} *
            <input
              className="input"
              value={form.sku}
              onChange={(e) => set('sku', e.target.value)}
            />
          </label>
        </div>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_gtin')}
            <input
              className="input"
              value={form.gtin ?? ''}
              onChange={(e) => set('gtin', e.target.value || null)}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_article_number')}
            <input
              className="input"
              value={form.articleNumberSupplier ?? ''}
              onChange={(e) =>
                set('articleNumberSupplier', e.target.value || null)
              }
            />
          </label>
        </div>
      </div>

      {/* Packaging */}
      <div className="form-card">
        <h2 className="form-card__title">
          {t('shipment_item_section_packaging')}
        </h2>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_units')}
            <input
              className="input"
              type="number"
              min={0}
              value={form.units ?? ''}
              onChange={(e) => set('units', intOrNull(e.target.value))}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_units_per_carton')}
            <input
              className="input"
              type="number"
              min={0}
              value={form.unitsPerCarton ?? ''}
              onChange={(e) => set('unitsPerCarton', intOrNull(e.target.value))}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_cartons')}
            <input
              className="input"
              type="number"
              min={0}
              value={form.cartons ?? ''}
              onChange={(e) => {
                setCartonsOverridden(true);
                set('cartons', intOrNull(e.target.value));
              }}
            />
            {calculatedCartons !== null &&
              (form.cartons !== calculatedCartons ? (
                <span className="form-label__note form-label__note--warning">
                  {t('shipment_item_cartons_overridden', {
                    count: calculatedCartons,
                  })}{' '}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => {
                      setCartonsOverridden(false);
                      set('cartons', calculatedCartons);
                    }}
                  >
                    {t('shipment_item_cartons_reset')}
                  </button>
                </span>
              ) : partialCartonUnits > 0 ? (
                <span className="form-label__note">
                  {t('shipment_item_cartons_partial', {
                    units: partialCartonUnits,
                    perCarton: form.unitsPerCarton,
                  })}
                </span>
              ) : null)}
          </label>
          <label className="form-label">
            {t('shipment_item_field_weight_per_carton')}
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={form.weightPerCartonKg ?? ''}
              onChange={(e) =>
                set('weightPerCartonKg', numOrNull(e.target.value))
              }
            />
          </label>
        </div>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_length')}
            <input
              className="input"
              type="number"
              step="0.1"
              min={0}
              value={form.lengthCm ?? ''}
              onChange={(e) => set('lengthCm', numOrNull(e.target.value))}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_width')}
            <input
              className="input"
              type="number"
              step="0.1"
              min={0}
              value={form.widthCm ?? ''}
              onChange={(e) => set('widthCm', numOrNull(e.target.value))}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_height')}
            <input
              className="input"
              type="number"
              step="0.1"
              min={0}
              value={form.heightCm ?? ''}
              onChange={(e) => set('heightCm', numOrNull(e.target.value))}
            />
          </label>
        </div>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_gross_weight')}
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={form.grossWeightKg ?? ''}
              onChange={(e) => set('grossWeightKg', numOrNull(e.target.value))}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_net_weight')}
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={form.netWeightKg ?? ''}
              onChange={(e) => set('netWeightKg', numOrNull(e.target.value))}
            />
          </label>
        </div>
      </div>

      {/* Pricing */}
      <div className="form-card form-card--pricing">
        <h2 className="form-card__title">
          {t('shipment_item_section_pricing')}
        </h2>
        <p className="form-card__subtitle">{t('shipment_item_pricing_hint')}</p>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_unit_price')}
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={form.singleUnitPrice ?? ''}
              onChange={(e) =>
                set('singleUnitPrice', numOrNull(e.target.value))
              }
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_carton_price')}
            <input
              className="input"
              type="number"
              step="0.01"
              min={0}
              value={cartonPriceDisplay}
              onChange={(e) => handleCartonPriceChange(e.target.value)}
            />
          </label>
        </div>
        {totalItemValue !== null && (
          <p className="shipment-item-form__total-value">
            {t('shipment_item_total_value')}: {totalItemValue}
          </p>
        )}
      </div>

      {/* Customs */}
      <div className="form-card">
        <h2 className="form-card__title">
          {t('shipment_item_section_customs')}
        </h2>
        <label className="form-label">
          {t('shipment_item_field_customs_description')}
          <textarea
            className="input"
            rows={3}
            value={form.customsDescription ?? ''}
            onChange={(e) => set('customsDescription', e.target.value || null)}
          />
        </label>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_main_material')}
            <input
              className="input"
              value={form.mainMaterialComposition ?? ''}
              onChange={(e) =>
                set('mainMaterialComposition', e.target.value || null)
              }
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_intended_use')}
            <input
              className="input"
              value={form.intendedUse ?? ''}
              onChange={(e) => set('intendedUse', e.target.value || null)}
            />
          </label>
        </div>
        <div className="form-row">
          <label className="form-label">
            {t('shipment_item_field_hs_code')}
            <input
              className="input"
              maxLength={6}
              value={form.hsCode6 ?? ''}
              onChange={(e) => set('hsCode6', e.target.value || null)}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_cn_code')}
            <input
              className="input"
              maxLength={8}
              value={form.cnCode8 ?? ''}
              onChange={(e) => set('cnCode8', e.target.value || null)}
            />
          </label>
          <label className="form-label">
            {t('shipment_item_field_taric_code')}
            <input
              className="input"
              maxLength={10}
              value={form.taricCode10 ?? ''}
              onChange={(e) => set('taricCode10', e.target.value || null)}
            />
          </label>
        </div>
        <a
          href="https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp"
          target="_blank"
          rel="noreferrer"
          className="link"
        >
          {t('shipment_item_taric_search_link')}
        </a>
      </div>

      {/* Footer */}
      <div className="form-footer">
        {editing ? (
          <>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              {t('delete')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveItem}
              disabled={saving}
            >
              {t('shipment_item_save_button')}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-secondary"
              onClick={goBack}
              disabled={saving}
            >
              {t('back')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={goBack}
              disabled={saving}
            >
              {t('close')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAddItem}
              disabled={saving}
            >
              {t('shipment_item_add_button')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ShipmentItemFormPage;
